import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { UserProfile } from '../../types'
import { Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Skeleton } from '../../components/ui'
import { ShieldIcon } from '../../components/icons'

/* Port of admin/private_works.html (admin/routes.py private_works() /
 * private_promote() / private_demote()). A "private" user's operational
 * records (loads, expenses, trucks, hires) are hidden from other roles in
 * the same organisation - see privacy_service.py's filter_private(), used
 * throughout the dispatch/finance/trucks endpoints. The activity-count
 * rollup the Jinja page shows (loads/expenses/trucks/hires/incidents/
 * hazards/production touched by private users) has no JSON API yet and is
 * left for a future pass - this page covers the core promote/demote flow. */

interface PrivateListResponse { ok: boolean; items: UserProfile[] }
interface AllUsersResponse { ok: boolean; items: UserProfile[] }

export default function AdminPrivateUsers() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')

  const privateQuery = useQuery({
    queryKey: ['admin', 'private'],
    queryFn: () => api.get<PrivateListResponse>('/admin/private'),
  })
  const allQuery = useQuery({
    queryKey: ['admin', 'users', 'all-for-private'],
    queryFn: () => api.get<AllUsersResponse>('/admin/users?status=&limit=1000'),
  })

  const toggle = useMutation({
    mutationFn: ({ uid, isPrivate }: { uid: string; isPrivate: boolean }) =>
      api.post(`/admin/users/${uid}/private`, { is_private: isPrivate }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'private'] })
      void qc.invalidateQueries({ queryKey: ['admin', 'users', 'all-for-private'] })
    },
  })

  const privateUsers = privateQuery.data?.items ?? []
  const privateUids = new Set(privateUsers.map((u) => u.uid))
  const q = search.trim().toLowerCase()
  const publicUsers = (allQuery.data?.items ?? [])
    .filter((u) => !privateUids.has(u.uid))
    .filter((u) => !q || (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
    .slice(0, 50)

  const isLoading = privateQuery.isLoading || allQuery.isLoading

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Private users</h1>
          <p className="page-head__sub">
            A private user's loads, expenses, trucks and hires stay hidden from other roles in their organisation.
          </p>
        </div>
      </div>

      {(privateQuery.isError || allQuery.isError) && (
        <div style={{ marginBottom: 'var(--sp-5)' }}><Alert tone="danger">Could not load users.</Alert></div>
      )}

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardHeader title="Currently private" sub={privateUsers.length ? `${privateUsers.length} user(s)` : undefined} />
        <CardBody flush>
          {isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
          ) : privateUsers.length === 0 ? (
            <EmptyState icon={<ShieldIcon size={22} />} title="No private users" body="Users marked private will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Name</th><th>Email</th><th>Role</th><th /></tr></thead>
                <tbody>
                  {privateUsers.map((u) => (
                    <tr key={u.uid}>
                      <td style={{ fontWeight: 600 }}>{u.full_name || '—'}</td>
                      <td>{u.email}</td>
                      <td>{titleCase(u.role)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Button size="sm" variant="ghost"
                                loading={toggle.isPending && toggle.variables?.uid === u.uid}
                                onClick={() => toggle.mutate({ uid: u.uid, isPrivate: false })}>
                          Make public
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Mark someone private" />
        <CardBody>
          <Input placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)}
                 style={{ marginBottom: 'var(--sp-4)' }} />
          {isLoading ? (
            <div className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
          ) : !q ? (
            <p style={{ color: 'var(--text-muted)' }}>Search for a user to mark private.</p>
          ) : publicUsers.length === 0 ? (
            <EmptyState title="No matches" body="" />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Name</th><th>Email</th><th>Role</th><th /></tr></thead>
                <tbody>
                  {publicUsers.map((u) => (
                    <tr key={u.uid}>
                      <td style={{ fontWeight: 600 }}>{u.full_name || '—'}</td>
                      <td>{u.email}</td>
                      <td>{titleCase(u.role)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Button size="sm" variant="ghost"
                                loading={toggle.isPending && toggle.variables?.uid === u.uid}
                                onClick={() => toggle.mutate({ uid: u.uid, isPrivate: true })}>
                          Mark private
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
